import { useState, useEffect } from 'react';
import { api } from './api';

/* Editable Page Content (Admin -> Page Content).
   The public API returns ONLY the sections an admin has customized.
   Everything else keeps its built-in approved wording, so nothing on the
   site changes until Steve edits and saves a section. */

let cache = null;
let inflight = null;
const listeners = new Set();

const fetchContent = () => {
  if (cache) return Promise.resolve(cache);
  inflight = inflight || api.get('/page-content')
    .then((r) => { cache = r.data || {}; return cache; })
    .catch(() => { cache = { sections: {}, rich: {}, home: null, footer_buttons: null }; return cache; });
  return inflight;
};

// On-page editing (Phase 96): after an admin saves a section, refetch the
// public content and notify every mounted component so the page that is being
// edited updates immediately without a reload.
export const refreshPageContent = async () => {
  cache = null;
  inflight = null;
  const fresh = await fetchContent();
  listeners.forEach((notify) => notify(fresh));
  return fresh;
};

export const usePageContent = () => {
  const [data, setData] = useState(cache);
  useEffect(() => {
    let alive = true;
    const notify = (c) => { if (alive) setData(c); };
    // ALWAYS subscribe so refreshPageContent() (on-page editing saves) can
    // update every mounted component, even ones mounted after the first load.
    listeners.add(notify);
    if (cache) setData(cache);
    else fetchContent().then(notify);
    return () => { alive = false; listeners.delete(notify); };
  }, []);
  return data || { sections: {}, rich: {}, home: null, footer_buttons: null };
};

// Returns the admin override {kicker, title, intro} for a section key like
// 'contact.header', or null when the built-in wording should be used.
// For hero-enabled pages the API returns the EFFECTIVE merged config
// (defaults + admin edits) including image/buttons/overlay fields.
export const useSectionOverride = (sectionKey) => {
  const content = usePageContent();
  if (!sectionKey) return null;
  return (content.sections || {})[sectionKey] || null;
};

// WYSIWYG rich text sections (Phase 93). Returns the sanitized admin HTML for
// a key like 'platform-sections.intro', or '' when the built-in wording stays.
export const useRichSection = (sectionKey) => {
  const content = usePageContent();
  if (!sectionKey) return '';
  return (content.rich || {})[sectionKey] || '';
};
